import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ProfileModal from '../Profile/ProfileModal';

const BUDGET_NAV = [
  {
    path: '/gestion-budget/dashboard',
    label: 'Tableau de bord',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" rx="1.5"/>
        <rect x="14" y="3" width="7" height="7" rx="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5"/>
        <rect x="14" y="14" width="7" height="7" rx="1.5"/>
      </svg>
    ),
  },
  {
    path: '/gestion-budget/transactions',
    label: 'Transactions',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
        <path d="M3 9l4-4 4 4M7 5v14M21 15l-4 4-4-4m4 4V5"/>
      </svg>
    ),
  },
  {
    path: '/gestion-budget/subscriptions',
    label: 'Abonnements',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"/>
        <path d="M12 6v6l4 2"/>
      </svg>
    ),
  },
  {
    path: '/gestion-budget/charts',
    label: 'Graphiques',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
        <path d="M18 20V10M12 20V4M6 20v-6"/>
      </svg>
    ),
  },
];

const BET_NAV = [
  {
    path: '/gestion-bet/recap',
    label: 'Récap',
    icon: <span style={{ fontSize: 16, lineHeight: 1 }}>▦</span>,
  },
  {
    path: '/gestion-bet/transactions',
    label: 'Transactions',
    icon: <span style={{ fontSize: 16, lineHeight: 1 }}>⇅</span>,
  },
  {
    path: '/gestion-bet/graphiques',
    label: 'Graphiques',
    icon: <span style={{ fontSize: 16, lineHeight: 1 }}>∿</span>,
  },
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const menuRef = useRef(null);

  const isBet = location.pathname.startsWith('/gestion-bet');
  const navItems = isBet ? BET_NAV : BUDGET_NAV;
  const isLauncher = location.pathname === '/home';
  const logoSrc = isLauncher
    ? '/dub-hub-logo.svg'
    : isBet
      ? '/logo-winamax-gestion.svg'
      : '/logo-budget-gestion.svg';

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside className={`sidebar${isBet ? ' winamax' : ''}`}>
      <button className="sidebar-logo" onClick={() => navigate('/home')}>
        <img src={logoSrc} alt="logo" className="logo-icon" />
      </button>

      {!isLauncher && (
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button
              key={item.path}
              className={`nav-item${location.pathname === item.path ? ' active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      )}

      <div className="sidebar-footer" ref={menuRef}>
        <button className="user-trigger" onClick={() => setMenuOpen(o => !o)}>
          <div className="avatar">{getInitials(user?.name)}</div>
          <div className="user-info">
            <span className="user-name">{user?.name || 'Utilisateur'}</span>
            <span className="user-email">{user?.email || ''}</span>
          </div>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
            className={`chevron${menuOpen ? ' open' : ''}`}>
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>

        {menuOpen && (
          <div className="user-dropdown">
            <button className="dropdown-item" onClick={() => { setShowProfile(true); setMenuOpen(false); }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
              Mon Profil
            </button>
            <div className="dropdown-sep" />
            <button className="dropdown-item danger" onClick={handleLogout}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                <path d="M17 16l4-4-4-4M21 12H9M13 4H5a2 2 0 00-2 2v12a2 2 0 002 2h8"/>
              </svg>
              Déconnexion
            </button>
          </div>
        )}
      </div>

      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
    </aside>
  );
};

export default Sidebar;
